import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import TruncatedText from "../../Components/TruncatedText";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { API_BASE } from "../../config/api";
import Pagination from "../../Components/Pagination";
import ProductSelect from "../../Components/ProductSelect";
import { usePosSettings } from "../../utils/posSettings";

interface Inventory {
  inventory_id: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  product_id: number;
  product_name?: string;
  quantity_added: number;
  quantity_removed: number;
  update_reason: string;
  created_at: string;
  updated_at: string;
  updated_by?: number;
  updated_by_name?: string;
  updated_by_email?: string;
}

interface EditInventory {
  inventory_id?: number;
  user_id?: number;
  product_id?: number;
  product_name?: string;
  quantity_added: string | number;
  quantity_removed: string | number;
  update_reason: string;
}

interface Product {
  product_id: number;
  product_name: string;
}

interface User {
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  email: string;
  user_type: string;
}

const InventoryPage = () => {
  const [inventories, setInventories] = useState<Inventory[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedInventories, setSelectedInventories] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editInventory, setEditInventory] = useState<EditInventory>({
    quantity_added: "",
    quantity_removed: "",
    update_reason: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => { setPage(1); }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchProducts();
    fetchUsers();
  }, []);

  const settings = usePosSettings();
  const pageSize = settings.itemsPerPage || 25;

  const fetchInventories = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/inventory?${params}`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const result = await response.json();
        setInventories(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalItems(result.total || 0);
      } else {
        console.error("Failed to fetch inventories. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching inventories:", error);
    }
  }, [cookies.auth, page, pageSize, debouncedSearchTerm]);

  useEffect(() => { fetchInventories(); }, [fetchInventories]);

  const fetchProducts = useCallback(async () => {
    try {
      // Only used to resolve product_id -> name for display/dropdowns here,
      // not the page's own paginated list — request the max page size
      // rather than paginating a lookup.
      const response = await authenticatedFetch(`${API_BASE}/product?page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const result = await response.json();
        setProducts(result.data || []);
      } else {
        console.error("Failed to fetch products. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, [cookies.auth]);

  const fetchUsers = useCallback(async () => {
    try {
      // Lookup only (resolve user_id -> name), same reasoning as fetchProducts above.
      const response = await authenticatedFetch(`${API_BASE}/user/users?page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setUsers(data?.users || []);
      } else {
        console.error("Failed to fetch users. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [cookies.auth]);

  const handleAdd = () => {
    setEditInventory({
      quantity_added: "",
      quantity_removed: "",
      update_reason: ""
    });
    setShowAddModal(true);
  };

  const handleUpdate = () => {
    if (selectedInventories.length !== 1) {
      alert("Please select exactly one inventory record to update");
      return;
    }
    
    const inventoryToEdit = inventories.find(inventory => inventory.inventory_id === selectedInventories[0]);
    if (inventoryToEdit) {
      setEditInventory({
        inventory_id: inventoryToEdit.inventory_id,
        user_id: inventoryToEdit.user_id,
        product_id: inventoryToEdit.product_id,
        product_name: inventoryToEdit.product_name || getProductName(inventoryToEdit.product_id),
        quantity_added: inventoryToEdit.quantity_added,
        quantity_removed: inventoryToEdit.quantity_removed,
        update_reason: inventoryToEdit.update_reason
      });
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    if (selectedInventories.length === 0) {
      alert("Please select at least one inventory record to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const inventoryId of selectedInventories) {
        const response = await authenticatedFetch(`${API_BASE}/inventory/${inventoryId}`, {
          method: "DELETE"
        }, cookies.auth);
        
        if (!response.ok) {
          console.error(`Failed to delete inventory ${inventoryId}. Status:`, response.status);
        }
      }
      setSelectedInventories([]);
      setShowDeleteConfirm(false);
      await fetchInventories();
      snack.onResponse({
        message: "Inventory record(s) deleted successfully!",
        status: 200,
      });
    } catch (error) {
      console.error("Error deleting inventories:", error);
      snack.onResponse({
        message: "Error deleting inventory record(s)",
        status: 500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);

      // Validate required fields
      if (!editInventory.product_id || editInventory.product_id === undefined) {
        alert("Please select a product");
        return;
      }

      if (!editInventory.update_reason.trim()) {
        alert("Update reason is required");
        return;
      }

      // Validate that at least one quantity field has a value
      const quantityAdded = parseFloat(editInventory.quantity_added.toString()) || 0;
      const quantityRemoved = parseFloat(editInventory.quantity_removed.toString()) || 0;

      if (quantityAdded === 0 && quantityRemoved === 0) {
        alert("Please enter either quantity added or quantity removed");
        return;
      }

      const productId = parseInt(editInventory.product_id.toString());
      const userId = isEdit ? parseInt(editInventory.user_id!.toString()) : (cookies.auth?.user_id || 1);
      
      if (isNaN(productId)) {
        alert("Invalid product ID");
        return;
      }

      const payload = {
        user_id: userId,
        product_id: productId,
        quantity_added: quantityAdded,
        quantity_removed: quantityRemoved,
        update_reason: editInventory.update_reason.trim(),
        updated_by: cookies.auth?.user_id || userId
      };

      const url = isEdit 
        ? `${API_BASE}/inventory/${editInventory.inventory_id}`
        : `${API_BASE}/inventory`;
      
      const method = isEdit ? "PUT" : "POST";

      console.log("=== INVENTORY SAVE DEBUG ===");
      console.log("URL:", url);
      console.log("Method:", method);
      console.log("Payload:", payload);
      console.log("Auth token:", cookies.auth?.sessionToken);
      console.log("Auth user_id:", cookies.auth?.user_id);
      console.log("Full auth cookie:", cookies.auth);

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': cookies.auth?.user_id || userId
        },
        body: JSON.stringify(payload)
      }, cookies.auth);

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (response.ok) {
        const responseData = await response.json();
        console.log("Success response:", responseData);

        setShowEditModal(false);
        setShowAddModal(false);
        setEditInventory({
          quantity_added: "",
          quantity_removed: "",
          update_reason: ""
        });
        setSelectedInventories([]);
        await fetchInventories();
        snack.onResponse({
          message: "Inventory record saved successfully!",
          status: 200,
        });
      } else {
        const errorText = await response.text();
        console.error("Failed to save inventory. Status:", response.status, "Error:", errorText);
        snack.onResponse({
          message: `Failed to save inventory: ${errorText}`,
          status: response.status,
        });
      }
    } catch (error) {
      console.error("Error saving inventory:", error);
      snack.onResponse({
        message: `Error saving inventory: ${error instanceof Error ? error.message : String(error)}`,
        status: 500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedInventories(inventories.map(inventory => inventory.inventory_id));
    } else {
      setSelectedInventories([]);
    }
  };

  const handleSelectInventory = (inventoryId: number, checked: boolean) => {
    if (checked) {
      setSelectedInventories([inventoryId]);
    } else {
      setSelectedInventories(selectedInventories.filter(id => id !== inventoryId));
    }
  };


  const getProductName = (productId: number | null | undefined) => {
    if (!productId) return "Unknown";
    const product = products.find(p => p.product_id === productId);
    return product ? product.product_name : "Unknown";
  };

  const getUserName = (userId: number | null | undefined) => {
    console.log("=== GET USER NAME DEBUG ===");
    console.log("Looking for userId:", userId);
    console.log("Available users:", users);
    console.log("Users array length:", users.length);
    
    if (!userId) {
      console.log("No userId provided, returning Unknown");
      return "Unknown";
    }
    
    const user = users.find(u => u.user_id === userId);
    console.log("Found user:", user);
    
    if (user) {
      const fullName = `${user.user_first_name} ${user.user_last_name}`;
      console.log("Returning full name:", fullName);
      return fullName;
    } else {
      console.log("User not found, returning Unknown");
      return "Unknown";
    }
  };

  const getUpdatedByUserName = (inventory: Inventory) => {
    // First try to use the updated_by_name if available
    if (inventory.updated_by_name) {
      return inventory.updated_by_name;
    }
    
    // Fall back to looking up by updated_by
    if (inventory.updated_by) {
      return getUserName(inventory.updated_by);
    }
    
    // If no updated_by info, return the original user name
    return inventory.user_name || getUserName(inventory.user_id);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  return (
    <div className={style.wrapper}>
      {/* Header Section */}
      <div className={style.header}>
        <h1 className={style.title}>Inventory</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by update reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canManageInventory && (
            <div className={style.actionButtons}>
              <button className={style.actionBtn} onClick={handleAdd}>
                Add
              </button>
              <button className={style.actionBtn} onClick={handleUpdate}>
                Update
              </button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canManageInventory && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedInventories.length === inventories.length && inventories.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Inventory ID</th>
              <th>User</th>
              <th>Product</th>
              <th>Quantity Added</th>
              <th>Quantity Removed</th>
              <th>Update Reason</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Updated By</th>
            </tr>
          </thead>
          <tbody>
            {inventories.map((inventory) => (
              <tr key={inventory.inventory_id} className={style.tableRow}>
                {permissions.canManageInventory && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedInventories.includes(inventory.inventory_id)}
                      onChange={(e) => handleSelectInventory(inventory.inventory_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>{inventory.inventory_id}</td>
                <td>{inventory.user_name || getUserName(inventory.user_id)}</td>
                <td>{inventory.product_name || getProductName(inventory.product_id)}</td>
                <td>{inventory.quantity_added}</td>
                <td>{inventory.quantity_removed}</td>
                <td className={style.descriptionCell}>
                  <TruncatedText text={inventory.update_reason} maxLength={50} />
                </td>
                <td>{formatDate(inventory.created_at)}</td>
                <td>{formatDate(inventory.updated_at)}</td>
                <td>{inventory.updated_by_name || getUpdatedByUserName(inventory)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {/* Add Modal */}
      {showAddModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Add Inventory Record</h2>
            <div className={style.formGroup}>
              <label>Product:</label>
              <ProductSelect
                value={editInventory.product_id}
                valueLabel={editInventory.product_name}
                onChange={(product) => setEditInventory({ ...editInventory, product_id: product.product_id, product_name: product.product_name })}
                placeholder="Search products by name..."
              />
            </div>
            <div className={style.formGroup}>
              <label>Quantity Added:</label>
              <input
                type="number"
                value={editInventory.quantity_added}
                onChange={(e) => setEditInventory({...editInventory, quantity_added: e.target.value})}
                className={style.input}
                placeholder="Enter quantity added (0 if none)"
                min="0"
                step="1"
              />
            </div>
            <div className={style.formGroup}>
              <label>Quantity Removed:</label>
              <input
                type="number"
                value={editInventory.quantity_removed}
                onChange={(e) => setEditInventory({...editInventory, quantity_removed: e.target.value})}
                className={style.input}
                placeholder="Enter quantity removed (0 if none)"
                min="0"
                step="1"
              />
            </div>
            <div className={style.formGroup}>
              <label>Update Reason:</label>
              <textarea
                value={editInventory.update_reason}
                onChange={(e) => setEditInventory({...editInventory, update_reason: e.target.value})}
                className={style.input}
                placeholder="Enter reason for inventory update *"
                required
                rows={3}
              />
            </div>
            <div className={style.modalActions}>
              <button onClick={() => setShowAddModal(false)} className={style.cancelBtn}>
                Cancel
              </button>
              <button onClick={() => handleSave(false)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Edit Inventory Record</h2>
            <div className={style.formGroup}>
              <label>Product:</label>
              <ProductSelect
                value={editInventory.product_id}
                valueLabel={editInventory.product_name}
                onChange={(product) => setEditInventory({ ...editInventory, product_id: product.product_id, product_name: product.product_name })}
                placeholder="Search products by name..."
              />
            </div>
            <div className={style.formGroup}>
              <label>Quantity Added:</label>
              <input
                type="number"
                value={editInventory.quantity_added}
                onChange={(e) => setEditInventory({...editInventory, quantity_added: e.target.value})}
                className={style.input}
                placeholder="Enter quantity added (0 if none)"
                min="0"
                step="1"
              />
            </div>
            <div className={style.formGroup}>
              <label>Quantity Removed:</label>
              <input
                type="number"
                value={editInventory.quantity_removed}
                onChange={(e) => setEditInventory({...editInventory, quantity_removed: e.target.value})}
                className={style.input}
                placeholder="Enter quantity removed (0 if none)"
                min="0"
                step="1"
              />
            </div>
            <div className={style.formGroup}>
              <label>Update Reason:</label>
              <textarea
                value={editInventory.update_reason}
                onChange={(e) => setEditInventory({...editInventory, update_reason: e.target.value})}
                className={style.input}
                placeholder="Enter reason for inventory update *"
                required
                rows={3}
              />
            </div>
            <div className={style.modalActions}>
              <button onClick={() => setShowEditModal(false)} className={style.cancelBtn}>
                Cancel
              </button>
              <button onClick={() => handleSave(true)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete the selected inventory record(s)?</p>
            <div className={style.modalActions}>
              <button onClick={() => setShowDeleteConfirm(false)} className={style.cancelBtn}>
                Cancel
              </button>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;